import React, { useState } from 'react';
import { Grid, Button, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ContentCopy, OpenInFull, Check } from '@mui/icons-material';
import config from '../../../../config';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-sh';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import ApiCurlExpandedDialog from './ApiCurlExpandedDialog';

function ApiCurlDisplay({ placeholders, values, projectId, componentId, isTest = false }) {
  const theme = useTheme();
  const [expandedDialogOpen, setExpandedDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const generateCurlCommand = () => {
    if (!placeholders) return '';

    const payload = {
      workflowId: projectId,
      componentId: componentId,
      placeholderValues: {},
    };

    // Add placeholder values to payload - show all placeholders with their current values or sample values
    if (placeholders && Array.isArray(placeholders)) {
      placeholders
        .filter(placeholder => placeholder.autogenerated !== true)
        .forEach(placeholder => {
          const key = placeholder.key;

          // Generate sample values based on placeholder type only
          const getSampleValue = placeholder => {
            const type = placeholder.type;

            switch (type) {
              case 'list':
                return ['sample_value_1', 'sample_value_2'];
              case 'datetime':
                return '2024-01-15T10:30:00.000Z';
              case 'number':
                return 123;
              case 'boolean':
                return true;
              case 'html':
                return '<p>Sample HTML content</p>';
              default:
                // Check if defaultValue indicates it's an array
                if (
                  typeof placeholder.defaultValue === 'object' &&
                  Array.isArray(placeholder.defaultValue)
                ) {
                  return ['sample_value_1', 'sample_value_2'];
                }
                return 'sample_value';
            }
          };

          // Helper function to check if a value is meaningful (not empty)
          const hasActualValue = value => {
            if (value === null || value === undefined) return false;
            if (typeof value === 'string' && value.trim() === '') return false;
            if (Array.isArray(value) && value.length === 0) return false;
            if (typeof value === 'object' && !Array.isArray(value)) {
              // For complex objects, check if they have meaningful extracted values
              const extractedValue = value.value !== undefined ? value.value : value.rawValue;
              return hasActualValue(extractedValue);
            }
            return true;
          };

          // Helper function to extract simple value from complex objects
          const getSimpleValue = value => {
            if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
              return value.value !== undefined ? value.value : value.rawValue;
            }
            return value;
          };

          // Determine final value: use actual value if meaningful, otherwise use sample
          let finalValue;
          const userValue = values && values[key];

          if (userValue && hasActualValue(userValue)) {
            finalValue = getSimpleValue(userValue);
          } else {
            finalValue = getSampleValue(placeholder);
          }

          payload.placeholderValues[key] = finalValue;
        });
    }

    const curlCommand = `curl -X POST "https://rest.${config.ROOT_DOMAIN}/v1/workflow/run" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_WORKFLOW86_API_KEY" \\
  -d '${JSON.stringify(payload, null, 2)}'`;

    return curlCommand;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generateCurlCommand());
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleExpand = () => {
    setExpandedDialogOpen(true);
  };

  return (
    <Grid container gap={2}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCopy}
              startIcon={copySuccess ? <Check /> : <ContentCopy />}
              sx={{
                color: copySuccess ? 'success.main' : undefined,
                borderColor: copySuccess ? 'success.main' : undefined,
                '&:hover': {
                  borderColor: copySuccess ? 'success.main' : undefined,
                  backgroundColor: copySuccess ? 'success.light' : undefined,
                },
              }}
            >
              {copySuccess ? 'Copied!' : 'Copy'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleExpand}
              startIcon={<OpenInFull />}
            >
              Expand
            </Button>
          </Box>
        </Box>
        <div id="aceditor" style={{ width: '100%', height: '600px' }}>
          <AceEditor
            mode="sh"
            theme={theme.palette.mode === 'dark' ? 'monokai' : 'github'}
            width="100%"
            height="100%"
            showPrintMargin={false}
            showGutter={true}
            wrapEnabled={true}
            highlightActiveLine={false}
            value={generateCurlCommand()}
            readOnly={true}
            fontSize={14}
            setOptions={{
              enableBasicAutocompletion: false,
              enableLiveAutocompletion: false,
              enableSnippets: false,
              showLineNumbers: true,
              tabSize: 2,
            }}
          />
        </div>
      </Grid>

      <ApiCurlExpandedDialog
        open={expandedDialogOpen}
        onClose={() => setExpandedDialogOpen(false)}
        curlCommand={generateCurlCommand()}
      />
    </Grid>
  );
}

export default ApiCurlDisplay;
